/**
 * FormPrintBlank — clean, print-only blank version of a form (no
 * answers, no navigation/buttons in the printed output). "Print blank
 * form" from FormDetail lands here.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import HandoverGate from "../components/layout/HandoverGate";
import { FormFieldRenderer } from "../components/forms/FormFieldRenderer";
import { StandardBlockRenderer } from "../components/forms/StandardBlockRenderer";
import { RichTextDisplay } from "../components/forms/RichTextDisplay";

const ANY_MEMBER_ENTITLEMENT = ["carer_free", "carer_profile", "carer_essentials", "pro_carer", "family_free", "family_circle"];

function FormPrintBlankInner() {
  const { templateId } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/forms/${templateId}`);
        if (alive) setForm(data);
      } catch (err) {
        if (alive) setError(formatApiError(err?.response?.data?.detail) || "This form is not available.");
      }
    })();
    return () => { alive = false; };
  }, [templateId]);

  if (error) return <div className="container-jcc py-24 text-center text-red-600" data-testid="form-print-blank-error">{error}</div>;
  if (!form) return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="form-print-blank-loading">Loading…</div>;

  return (
    <div className="container-jcc py-8" data-testid="form-print-blank-page">
      <div className="print:hidden flex items-center justify-between mb-6 max-w-3xl mx-auto">
        <Link to={`/forms/${templateId}`} className="btn-outline text-sm" data-testid="form-print-blank-back-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <button type="button" onClick={() => window.print()} className="btn-teal text-sm" data-testid="form-print-blank-print-btn">
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>
      <div className="max-w-3xl mx-auto bg-white">
        <h1 className="font-display text-2xl font-bold">{form.title}</h1>
        <RichTextDisplay content={form.description} className="mt-1 text-ink-muted" testId="form-print-blank-description" />
        {form.intro_text && (
          <div className="mt-3 border border-slate-200 rounded-lg p-3">
            <RichTextDisplay content={form.intro_text} testId="form-print-blank-intro" />
          </div>
        )}
        <div className="mt-5">
          <StandardBlockRenderer standardBlock={form.standard_block} answers={{}} mode="print" />
          {(form.fields || []).map((field) => (
            <FormFieldRenderer key={field.field_id} field={field} value={undefined} mode="print" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FormPrintBlank() {
  return (
    <HandoverGate requiredEntitlement={ANY_MEMBER_ENTITLEMENT}>
      <FormPrintBlankInner />
    </HandoverGate>
  );
}
